import React from 'react';
import {
  Navigate,
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
} from '@tanstack/react-router';
import AppFrame from './components/AppFrame';
import AuthScreen from './components/AuthScreen';
import HomeRedirect from './components/HomeRedirect';
import PluginPage from './components/PluginPage';
import { tildeDecode } from './definitions';
import { preloadPluginPage } from './pageLoading';
import { ChangePasswordEditorPage, ManageAdmins, ManageSubtenants, authScreens } from './screens';

/**
 * The CMS route table. Replaces the `$stateProvider` block in `web/app/app.js`.
 *
 * **Every URL is preserved exactly.** Admins have bookmarks; the rail's active-item logic
 * parses `/main/plugin/<plugin>/<page>` out of the path (`sidebar/definitions.js`); and the
 * server hands these paths out in the side menu. Two details are easy to lose:
 *
 * **The trailing slash is real.** The Angular state was
 * `/plugin/:pluginName/:pageName/:params` and the server always supplied `params: ''`, so the
 * canonical URL is `/main/plugin/url-manager/default/` with an empty final segment, which is
 * the shape the rail is fed in `sidebar/__tests__/render.test.js`. `trailingSlash: 'preserve'`
 * and the separate no-params route are both load-bearing.
 *
 * **Slashes inside a plugin name are escaped as `~2F`, not `%2F`.** `@uirouter/angularjs`
 * overrode the `path` param type to do that, to survive proxies that normalise `%2F`. It is
 * not cosmetic: `custom-plugins/iuoe/itrs`'s server code hard-codes the escaped form into URLs
 * that are mailed to users. See `tildeEncode` in `../definitions`.
 */
export const rootRoute = createRootRoute({ component: () => <Outlet /> });

const authRoute = (path, Screen, pass) =>
  createRoute({
    getParentRoute: () => rootRoute,
    path,
    component: () => <AuthScreen Screen={Screen} pass={pass} />,
  });

/**
 * The nine auth screens, which were already React inside the Angular route table. Each state
 * held one React element and a controller supplying `navTo` and `resetCfg`; `AuthScreen`
 * supplies both now, so the screens themselves are untouched.
 */
export const authRoutes = [
  authRoute('/login', authScreens.login),
  authRoute('/changePassword', authScreens.changePassword),
  authRoute('/changePasswordSuccess', authScreens.changePasswordSuccess),
  authRoute('/forgotPassword', authScreens.forgotPassword),
  authRoute('/forgotPasswordSuccess/$email', authScreens.forgotPasswordSuccess, 'email'),
  authRoute('/resetPassword/$promptId', authScreens.resetPassword, 'promptId'),
  authRoute('/resetPasswordSuccess', authScreens.resetPasswordSuccess),
  authRoute('/register/$promptId', authScreens.register, 'promptId'),
  authRoute('/registerSuccess', authScreens.registerSuccess),
];

/** The app frame: the rail, the growl container, and the page beside them. */
export const mainRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/main',
  component: AppFrame,
});

export const mainIndexRoute = createRoute({
  getParentRoute: () => mainRoute,
  path: '/',
  component: HomeRedirect,
});

/**
 * `/` is a hot path, not an edge case.
 *
 * It is the CMS entry point, it is where `tenantApi` sends the browser after every tenant or
 * subtenant switch (`window.location.replace('/')`), and the server 302s an
 * already-authenticated visitor there from `/login`. Angular covered it with
 * `$urlRouterProvider.otherwise('/main/')`, which rendered the frame.
 *
 * Without this route it fell to `defaultNotFoundComponent`, which renders at the **root**,
 * outside `AppFrame`: no rail, no redux provider, a white page until the config lands. This
 * redirects into the frame instead, and rewrites the URL the way ui-router did.
 */
export const rootIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: () => <Navigate to="/main/" replace />,
});

/**
 * A frame screen of its own, loaded on first open (`./screens`). Its `loader` starts the
 * download as the route matches, and again when the rail's pointer is over its item
 * (`AppFrame`'s `preloadMenuItem`). It returns nothing, so the navigation never waits on it.
 */
const mainScreen = (path, Screen) =>
  createRoute({
    getParentRoute: () => mainRoute,
    path,
    loader: () => {
      Screen.preload();
    },
    component: () => <Screen />,
  });

export const manageAdminsRoute = mainScreen('manage-admins', ManageAdmins);
export const manageSubtenantsRoute = mainScreen('manage-subtenants', ManageSubtenants);
/**
 * Change Password inside the frame, which is what the rail opens (SURF-4828). The standalone
 * `/changePassword` among `authRoutes` stays: a user with a temporary password is sent there
 * by the server on every request, so the frame never loads for them.
 */
export const changePasswordRoute = mainScreen('change-password', ChangePasswordEditorPage);

/**
 * Two routes rather than one optional segment. `/main/plugin/x/default/` is emitted on every
 * menu click, and declaring that shape explicitly is clearer than relying on optional-segment
 * matching to produce an empty string.
 */
/**
 * The page's code starts downloading as the route matches, alongside the plugin list
 * `PluginPage` waits for, instead of after it; and when the rail's pointer is over the item.
 * Nothing is returned, so the navigation never waits on it. See `./pageLoading`.
 */
const preloadPage = ({ params }) => {
  preloadPluginPage(tildeDecode(params.pluginName), params.pageName);
};

export const pluginRoute = createRoute({
  getParentRoute: () => mainRoute,
  path: 'plugin/$pluginName/$pageName/$params',
  loader: preloadPage,
  component: PluginPage,
});

export const pluginNoParamsRoute = createRoute({
  getParentRoute: () => mainRoute,
  path: 'plugin/$pluginName/$pageName',
  loader: preloadPage,
  component: PluginPage,
});

export const routeTree = rootRoute.addChildren([
  rootIndexRoute,
  ...authRoutes,
  mainRoute.addChildren([
    mainIndexRoute,
    manageAdminsRoute,
    manageSubtenantsRoute,
    changePasswordRoute,
    pluginRoute,
    pluginNoParamsRoute,
  ]),
]);

export const buildRouter = (options = {}) =>
  createRouter({
    routeTree,
    trailingSlash: 'preserve',
    // `$urlRouterProvider.otherwise('/main/')`: anything unmatched goes home. A redirect
    // rather than `HomeRedirect` itself, because an unmatched top-level URL renders at the
    // root, outside `AppFrame`, where there is no rail and no CMS context.
    defaultNotFoundComponent: () => <Navigate to="/main/" replace />,
    ...options,
  });
